/* eslint-disable */
import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import {
  Box, Typography, Divider, IconButton, CircularProgress,
} from '@mui/material';
/* Select, MenuItem, FormControl removed — using DropdownSelect + StyledField instead */
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Modal from 'Shared/Modal';
import AppButton from 'Shared/UI/Button';
import DropdownSelect from 'Shared/UI/DropdownSelect';
import Image from 'Shared/Image';
import { StyledField } from 'Components/AccountSettings/helpers';
import ConfigContext from 'Shared/ConfigContext';
import {
  setOpenPanierList,
  addProductToPanier,
  setPanierProducts,
  addPanier,
  initPaniers,
} from 'Shared/ConfigContext/Actions';
import toast from 'Utils/toast';
import {
  addProductCart, addCart, getCarts, getCartDetail, getPriceRange,
} from 'Services';
import useNetworkLoader from 'hooks/useNetworkLoader';
import useDebounce from 'hooks/useDebounce';
import { formatAmount, getStocks } from 'Utils/ulilities';
import { applyRemise } from './Available';
import noImage from '../../Images/no_image.svg';

const AddPanierModal = () => {
  const {
    dispatch,
    state: { paniers, panierListState, selectedProductTobeAdded },
  } = useContext(ConfigContext);

  const [selectedPanier, setSelectedPanier] = useState('');
  const [newPanierName, setNewPanierName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [adding, setAdding] = useState(false);
  const [rangePricing, setRangePricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const { callApi } = useNetworkLoader();
  const debouncedQuantity = useDebounce(quantity, 150);
  const latestRequestRef = useRef(0);

  const availablePaniers = (paniers || []).filter((p) => p.validated === 0);
  const product = selectedProductTobeAdded;

  useEffect(() => {
    if (panierListState) {
      callApi(getCarts, (data) => dispatch(initPaniers(data)), false);
    } else {
      setSelectedPanier('');
      setNewPanierName('');
      setQuantity(1);
    }
  }, [panierListState]);

  useEffect(() => {
    if (product?.gameQuantity) setQuantity(product.gameQuantity);
    setRangePricing(null);
    setPricingLoading(false);
  }, [product]);

  useEffect(() => {
    if (!product?.reference || !debouncedQuantity || debouncedQuantity < 1) return;
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setPricingLoading(true);
    callApi(
      () => getPriceRange(product.reference, debouncedQuantity),
      (data) => {
        // Ignore a response that arrives after a newer quantity change already fired.
        if (latestRequestRef.current !== requestId) return;
        setRangePricing(data);
        setPricingLoading(false);
      },
      false,
      () => { if (latestRequestRef.current === requestId) setPricingLoading(false); },
    );
  }, [product?.reference, debouncedQuantity]);

  const onClose = () => {
    dispatch(setOpenPanierList(false));
    setSelectedPanier('');
    setNewPanierName('');
    setQuantity(1);
    setRangePricing(null);
  };

  const addToExistingPanier = (panier) => {
    setAdding(true);
    callApi(
      () => getCartDetail(panier.id),
      (freshPanier) => {
        const freshProducts = freshPanier?.products || [];
        const existingProduct = freshProducts.find((p) => !p.gratuite && p.reference === product?.reference);
        if (!existingProduct && freshProducts.length >= 40) {
          toast.error('Vous ne pouvez pas ajouter plus de 40 produits sur la même commande.');
          setAdding(false);
          return;
        }
        const isExist = existingProduct || { quantity: 0 };
        callApi(
          () => addProductCart(panier.id, product.reference, isExist.quantity + quantity),
          (resp) => {
            toast.success('Produit ajouté au panier avec succès');
            if (paniers && paniers.length > 0) {
              dispatch(addProductToPanier(panier.name, quantity));
              if (resp?.products) {
                dispatch(setPanierProducts(panier.name, resp.products));
              }
            }
            setAdding(false);
            onClose();
          },
          false,
          () => setAdding(false),
        );
      },
      false,
      () => setAdding(false),
    );
  };

  const onHandleAdd = () => {
    const currPanier = availablePaniers.find((p) => p.name === selectedPanier);
    if (currPanier) {
      addToExistingPanier(currPanier);
    } else if (newPanierName.trim()) {
      const trimmedName = newPanierName.trim();
      setAdding(true);
      callApi(
        () => addCart(trimmedName),
        (cartId) => {
          dispatch(addPanier(trimmedName, cartId));
          callApi(
            () => addProductCart(cartId, product.reference, quantity),
            () => {
              dispatch(addProductToPanier(trimmedName, quantity));
              toast.success('Produit ajouté au nouveau panier avec succès');
              setAdding(false);
              onClose();
            },
            false,
            () => setAdding(false),
          );
        },
        false,
        () => setAdding(false),
      );
    } else {
      toast.info('Veuillez sélectionner un panier ou saisir un nom de nouveau panier.');
    }
  };

  /* product info — prefer the quantity-resolved Cloud tier price once it's loaded,
     fall back to the static catalog price while the lookup is in flight */
  const effectivePrice = rangePricing ? rangePricing.price : product?.price;
  const effectiveRemise = rangePricing ? rangePricing.remise : product?.remise;
  const effectiveRemiseExceptionnelle = rangePricing
    ? rangePricing.remiseExceptionnelle
    : product?.remiseExceptionnelle;
  const discountPct = product ? applyRemise(effectiveRemise, effectiveRemiseExceptionnelle) : 0;
  const discountedPrice = effectiveRemiseExceptionnelle
    ? Number((effectivePrice * (100 - discountPct)) / 100)
    : null;
  const stocks = product ? getStocks(
    product.stockDerbOmar, product.reservedDO,
    product.stockBouskoura, product.stockAinSebaa,
    product.stockCfcim, product.reserved,
  ) : {};
  const isAvailable = (stocks.stockDisponibleBouskoura || 0) > 0
    || (stocks.stockDisponibleDebOmar || 0) > 0;

  return (
    <Modal
      open={panierListState}
      setOpen={onClose}
      backdropSx={{ backgroundColor: 'rgba(0, 37, 59, 0.28)' }}
      sx={{
        borderRadius: '12px',
        border: 'none',
        outline: 'none',
        boxShadow: '0px 20px 60px rgba(0,0,0,0.15)',
        px: '24px',
        py:'16px',
        width: '485px !important',
        minHeight: '405px',
        top: '40%',
        left:'50%'
      }}
    >
      <Box>
        {/* Title */}
        <Typography sx={{
          fontSize: '20px',
          fontWeight: 600,
          color: '#385576',
          mb: '12px',
        }}>
          Ajouter au panier
        </Typography>

        {/* Product info card */}
        {product && (
          <Box sx={{
            display: 'flex',
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            overflow: 'hidden',
            mb: '12px',
            boxShadow: '0px 4px 12px rgba(0,0,0,0.06)',
          }}>
            {/* Product image */}
            <Box sx={{
              width: 130,
              backgroundColor: '#F8FAFC',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              borderRight: '1px solid #E2E8F0',
              position: 'relative',
              p: '8px',
            }}>
              <img
                src={product.image || noImage}
                alt={product.name}
                style={{ maxHeight: 70, maxWidth: 90, objectFit: 'contain' }}
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = noImage; }}
              />
              {product.brand?.logoImage && (
                <Box sx={{ position: 'absolute', bottom: 4, right: 4 }}>
                  <Image
                    src={product.brand.logoImage}
                    alt={product.brand.name}
                    style={{ maxHeight: 14, maxWidth: 45, objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Box>

            {/* Product details */}
            <Box sx={{ flex: 1, minWidth: 0, p: '8px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '2px' }}>
              {/* Brand + availability */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#385576', textTransform: 'uppercase' }}>
                  {product.brand?.name || ''}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                  <Box sx={{
                    width: 6, height: 6, borderRadius: '50%',
                    backgroundColor: isAvailable ? '#0AD69F' : '#EF456E',
                  }} />
                  <Typography sx={{
                    fontSize: '10px', fontWeight: 700,
                    color: isAvailable ? '#0AD69F' : '#EF456E',
                    textTransform: 'uppercase',
                  }}>
                    {isAvailable ? 'Disponible' : 'Non Disponible'}
                  </Typography>
                </Box>
              </Box>

              {/* Name + Price (price aligned with 2nd line of name) */}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                <Typography sx={{
                  fontSize: '14px', fontWeight: 600, color: '#000', lineHeight: 1.4,
                  flex: 1, minWidth: 0,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                }}>
                  {product.name}
                </Typography>
                <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                    {pricingLoading && <CircularProgress size={10} sx={{ color: '#007BC6' }} />}
                    <Typography sx={{
                      fontSize: '13px', fontWeight: 700, color: '#007BC6', whiteSpace: 'nowrap',
                      opacity: pricingLoading ? 0.45 : 1, transition: 'opacity 0.15s',
                    }}>
                      {formatAmount(discountedPrice !== null ? discountedPrice : effectivePrice, 2)} Dh
                    </Typography>
                  </Box>
                  {discountedPrice !== null && (
                    <Typography sx={{ fontSize: '10px', color: '#717182', fontWeight: 500, textDecoration: 'line-through', whiteSpace: 'nowrap' }}>
                      {formatAmount(product.price)} Dh
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Reference + Quantity on same row */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography sx={{ fontSize: '11px', fontWeight: 500 }}>
                    <Box component="span" sx={{ color: '#385576' }}>REF:</Box>{' '}
                    <Box component="span" sx={{ color: '#007BC6' }}>{product.reference}</Box>
                  </Typography>
                  {product.family?.name && (
                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#717182', textTransform: 'uppercase' }}>
                      {product.family.name}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  sx={{
                    border: '1px solid #DEE5EE',
                    borderRadius: '6px 0 0 6px',
                    width: 28, height: 28,
                    color: '#717182',
                  }}
                >
                  <RemoveIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Box
                  component="input"
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val >= 1) setQuantity(val);
                    else if (e.target.value === '') setQuantity('');
                  }}
                  onBlur={() => { if (quantity === '' || quantity < 1) setQuantity(1); }}
                  sx={{
                    width: 44, height: 28,
                    borderTop: '1px solid #DEE5EE', borderBottom: '1px solid #DEE5EE',
                    borderLeft: 'none', borderRight: 'none',
                    textAlign: 'center',
                    fontSize: '13px', fontWeight: 700, color: '#1A2035',
                    outline: 'none', background: 'transparent',
                    fontFamily: 'inherit',
                    '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { WebkitAppearance: 'none', m: 0 },
                    MozAppearance: 'textfield',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setQuantity((q) => q + 1)}
                  sx={{
                    border: '1px solid #DEE5EE',
                    borderRadius: '0 6px 6px 0',
                    width: 28, height: 28,
                    color: '#717182',
                  }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* Panier selector */}
        <DropdownSelect
          options={availablePaniers.map((p) => ({ value: p.name, label: p.name }))}
          value={selectedPanier}
          onChange={setSelectedPanier}
          placeholder="Sélectionner le panier"
          width="100%"
          disabled={availablePaniers.length === 0}
        />

        {/* Divider with text */}
        <Box sx={{ display: 'flex', alignItems: 'center', my: '10px', gap: '12px' }}>
          <Divider sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: '12px', color: '#A0AEC0', fontWeight: 500, whiteSpace: 'nowrap' }}>
            Ou ajouter a un nouveau panier
          </Typography>
          <Divider sx={{ flex: 1 }} />
        </Box>

        {/* New panier name */}
        <StyledField
          label="Nom du panier"
          value={newPanierName}
          onChange={(e) => setNewPanierName(e.target.value)}
        />

        {/* Buttons */}
        <Box sx={{ display: 'flex', gap: '12px', mt: '20px' }}>
          <AppButton
            variant="cancel"
            label="Annuler"
            fullWidth
            onClick={onClose}
            color="#FFFFFF"
            hoverColor="#F1F5F9"
            sx={{ flex: 1, boxShadow: '0px 4px 12px rgba(0,0,0,0.06)', gap: '4px', border: '1px solid #E6E7EA' }}
          />
          <AppButton
            variant="add"
            label="Ajouter"
            fullWidth
            loading={adding}
            disabled={!selectedPanier && !newPanierName.trim()}
            onClick={onHandleAdd}
            sx={{ flex: 1, boxShadow: '0px 4px 12px rgba(0,0,0,0.06)', gap: '4px' }}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default AddPanierModal;